/**
 * SpriteRenderer - 2D Sprite Animation Renderer
 * 
 * Renders 2D sprites based on 3D bone rotation data.
 * Uses canvas for compositing sprites in the correct z-order.
 * 
 * This is the core rendering component for the 2D Rigged Animation Pipeline.
 */

import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { getApiBaseUrl } from '@/config/api';

/**
 * Sprite scale factor - adjusts overall sprite size
 * This is a multiplier applied to all sprites uniformly
 */
const SPRITE_SCALE_FACTOR = 0.42;

/**
 * Bone pairs that define each body part's size
 * Each body part maps to [startBone, endBone] to calculate its length
 */
const BODY_PART_BONE_PAIRS = {
  head: ['Head', 'Neck'],
  torso: ['Neck', 'Hips'],
  arm_upper_left: ['LeftArm', 'LeftForeArm'],
  arm_upper_right: ['RightArm', 'RightForeArm'],
  arm_lower_left: ['LeftForeArm', 'LeftHand'],
  arm_lower_right: ['RightForeArm', 'RightHand'],
  hand_left: ['LeftHand', 'LeftHandMiddle1'],
  hand_right: ['RightHand', 'RightHandMiddle1'],
  leg_upper_left: ['LeftUpLeg', 'LeftLeg'],
  leg_upper_right: ['RightUpLeg', 'RightLeg'],
  leg_lower_left: ['LeftLeg', 'LeftFoot'],
  leg_lower_right: ['RightLeg', 'RightFoot'],
  foot_left: ['LeftFoot', 'LeftToeBase'],
  foot_right: ['RightFoot', 'RightToeBase']
};

/**
 * Calculate distance between two bones
 */
const getBoneDistance = (boneData, bone1, bone2) => {
  const b1 = boneData[bone1];
  const b2 = boneData[bone2];
  if (!b1 || !b2) return null;
  
  const dx = (b2.position?.x || 0) - (b1.position?.x || 0);
  const dy = (b2.position?.y || 0) - (b1.position?.y || 0);
  const dz = (b2.position?.z || 0) - (b1.position?.z || 0);
  
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
};

/**
 * Calculate the sprite scale for a body part based on bone distances
 */
const getBodyPartScale = (partName, boneData, sprite, characterHeight) => {
  const bonePair = BODY_PART_BONE_PAIRS[partName];
  if (!bonePair || !boneData) {
    return 0.15; // Default small scale
  }
  
  const boneDistance = getBoneDistance(boneData, bonePair[0], bonePair[1]);
  if (!boneDistance || boneDistance < 0.01) {
    return 0.15; // Default if bones not found
  }
  
  // Calculate model's total height for reference
  let minY = Infinity, maxY = -Infinity;
  for (const transform of Object.values(boneData)) {
    const y = transform.position?.y || 0;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  const modelHeight = Math.max(0.5, maxY - minY);
  
  // The bone distance as a fraction of model height
  const boneFraction = boneDistance / modelHeight;
  
  // Scale sprite height to match bone distance in screen space
  // Target: sprite height = boneFraction * characterHeight
  const targetHeight = boneFraction * characterHeight;
  let scale = targetHeight / sprite.height;
  
  // Apply learned scale adjustments for specific body parts
  const scaleAdjust = BODY_PART_SCALE_ADJUSTMENTS[partName];
  if (scaleAdjust) {
    scale *= scaleAdjust;
  }
  
  return Math.min(scale, 0.5); // Cap at 0.5 to prevent huge sprites
};

/**
 * Primary bone for each body part - used for position reference
 * We pick the bone that represents the "anchor" point for each sprite
 */
const PRIMARY_BONES = {
  head: 'Head',
  torso: 'Spine1',      // Middle of torso
  arm_upper_left: 'LeftArm',
  arm_upper_right: 'RightArm',
  arm_lower_left: 'LeftForeArm',
  arm_lower_right: 'RightForeArm',
  hand_left: 'LeftHand',
  hand_right: 'RightHand',
  leg_upper_left: 'LeftUpLeg',
  leg_upper_right: 'RightUpLeg',
  leg_lower_left: 'LeftLeg',
  leg_lower_right: 'RightLeg',
  foot_left: 'LeftFoot',
  foot_right: 'RightFoot'
};

/**
 * Default z-order for body parts (higher = in front)
 * For front-facing view:
 * - Right side limbs (viewer's left) are typically "back" limbs
 * - Left side limbs (viewer's right) are typically "front" limbs
 * - Torso is middle layer
 * - Head is on top
 * 
 * NOTE: This is the BASE z-order. Dynamic z-ordering will adjust these
 * values based on each bone's actual Z-depth relative to the camera.
 */
const DEFAULT_Z_ORDER = {
  // Back layer (behind torso)
  arm_upper_right: 1,
  arm_lower_right: 2,
  hand_right: 3,
  leg_upper_right: 1,
  leg_lower_right: 2,
  foot_right: 3,
  
  // Middle layer (torso)
  torso: 10,
  
  // Front layer (in front of torso)
  leg_upper_left: 11,
  leg_lower_left: 12,
  foot_left: 13,
  arm_upper_left: 14,
  arm_lower_left: 15,
  hand_left: 16,
  
  // Top layer
  head: 20
};

/**
 * Calculate dynamic z-order for a bone based on its world Z position
 * relative to the camera. Parts closer to camera get higher z-index.
 * 
 * @param {Object} bonePos - The bone's world position {x, y, z}
 * @param {Object} cameraPosition - Camera world position {x, y, z}
 * @param {Object} cameraTarget - Camera target position {x, y, z}
 * @param {string} partName - Body part name for base z-order
 * @returns {number} Dynamic z-index value
 */
const calculateDynamicZOrder = (bonePos, cameraPosition, cameraTarget, partName) => {
  if (!bonePos) {
    return DEFAULT_Z_ORDER[partName] || 10;
  }
  
  // Calculate camera forward direction (normalized)
  const camDirX = cameraTarget.x - cameraPosition.x;
  const camDirY = cameraTarget.y - cameraPosition.y;
  const camDirZ = cameraTarget.z - cameraPosition.z;
  const camDirLen = Math.sqrt(camDirX * camDirX + camDirY * camDirY + camDirZ * camDirZ);
  const camFwdX = camDirX / camDirLen;
  const camFwdY = camDirY / camDirLen;
  const camFwdZ = camDirZ / camDirLen;
  
  // Vector from camera to bone
  const toBoneX = bonePos.x - cameraPosition.x;
  const toBoneY = bonePos.y - cameraPosition.y;
  const toBoneZ = bonePos.z - cameraPosition.z;
  
  // Project bone position onto camera forward direction (dot product)
  // This gives us the "depth" of the bone along the camera's view axis
  const depthAlongCam = toBoneX * camFwdX + toBoneY * camFwdY + toBoneZ * camFwdZ;
  
  // Invert so that closer bones (smaller depth) get HIGHER z-index
  // Scale to reasonable range (multiply by 100 to get good spread)
  const baseZ = DEFAULT_Z_ORDER[partName] || 10;
  const dynamicZ = baseZ + Math.round(-depthAlongCam * 100);
  
  return dynamicZ;
};

/**
 * Bone hierarchy for IK - defines parent-child relationships
 * Used to calculate relative rotations and positions
 */
const BONE_HIERARCHY = {
  head: { parent: 'torso', bone: 'Head' },
  torso: { parent: null, bone: 'Spine1' },
  arm_upper_left: { parent: 'torso', bone: 'LeftArm' },
  arm_lower_left: { parent: 'arm_upper_left', bone: 'LeftForeArm' },
  hand_left: { parent: 'arm_lower_left', bone: 'LeftHand' },
  arm_upper_right: { parent: 'torso', bone: 'RightArm' },
  arm_lower_right: { parent: 'arm_upper_right', bone: 'RightForeArm' },
  hand_right: { parent: 'arm_lower_right', bone: 'RightHand' },
  leg_upper_left: { parent: 'torso', bone: 'LeftUpLeg' },
  leg_lower_left: { parent: 'leg_upper_left', bone: 'LeftLeg' },
  foot_left: { parent: 'leg_lower_left', bone: 'LeftFoot' },
  leg_upper_right: { parent: 'torso', bone: 'RightUpLeg' },
  leg_lower_right: { parent: 'leg_upper_right', bone: 'RightLeg' },
  foot_right: { parent: 'leg_lower_right', bone: 'RightFoot' }
};

/**
 * Default pivot points for body parts
 * Pivot determines where on the sprite the bone position anchors
 * [0,0] = top-left, [0.5,0.5] = center, [1,1] = bottom-right
 * 
 * LEARNED FROM USER: Pivots should be at the JOINT that connects to parent:
 * - Limbs: pivot at top (the joint connecting to parent body part)
 * - Torso: pivot at top (neck, connecting to head)
 */
const DEFAULT_PIVOTS = {
  head: [0.64, 0.77],               // Adjusted by user
  torso: [0.42, 0.90],              // Anchor at neck (top) - connects to head
  arm_upper_left: [0.49, 0.14],     // Anchor at shoulder joint (top)
  arm_upper_right: [0.73, 0.21],    // Anchor at shoulder joint (top)
  arm_lower_left: [0.64, 0.15],     // Anchor at elbow joint (top)
  arm_lower_right: [0.70, 0.09],    // Anchor at elbow joint (top)
  hand_left: [0.74, 0.24],          // Anchor at wrist joint (top)
  hand_right: [0.68, 0.13],         // Anchor at wrist joint (top)
  leg_upper_left: [0.57, 0.14],     // Anchor at hip joint (top)
  leg_upper_right: [0.55, 0.14],    // Anchor at hip joint (top)
  leg_lower_left: [0.5, 0.1],       // Anchor at knee joint (top)
  leg_lower_right: [0.5, 0.1],      // Anchor at knee joint (top)
  foot_left: [0.71, 0.21],          // Anchor at ankle joint (top)
  foot_right: [0.72, 0.20]          // Anchor at ankle joint (top)
};

/**
 * LEARNED FROM USER: Scale adjustments for body parts
 * Some parts need to be scaled differently than bone distance suggests
 */
const BODY_PART_SCALE_ADJUSTMENTS = {
  head: 1.05,           // Slightly larger
  leg_lower_left: 0.80, // Lower legs were too long
  leg_lower_right: 0.78
};

/**
 * LEARNED FROM USER: Position offsets to spread limbs correctly
 * These offsets help position sprites relative to the bone-calculated position
 * Note: These were tuned for an 800x600 canvas with scale 1.5 and characterHeight ~420px
 * The offsets should be scaled proportionally for different canvas sizes
 */
const LEARNED_OFFSETS = {
  head: { x: 5, y: -11 },
  torso: { x: -4, y: 10 },
  arm_upper_left: { x: -12, y: -6 },
  arm_lower_left: { x: -44, y: 5 },
  hand_left: { x: -78, y: 16 },
  arm_upper_right: { x: 35, y: -12 },
  arm_lower_right: { x: 61, y: -7 },
  hand_right: { x: 50, y: 12 },
  leg_upper_left: { x: -6, y: -14 },
  leg_upper_right: { x: 10, y: -16 },
  leg_lower_left: { x: -17, y: 6 },    // Was y:50, user adjusted by -44
  leg_lower_right: { x: 23, y: 3 },    // Was y:45, user adjusted by -42
  foot_left: { x: -18, y: 8 },         // Adjusted for better leg-foot connection
  foot_right: { x: 23, y: 2 }          // Adjusted for better leg-foot connection
};

/**
 * Reference canvas dimensions that the offsets were tuned for
 */
const REFERENCE_CHARACTER_HEIGHT = 420;

// Convert S3 URL to use backend proxy (avoids CORS issues with presigned URLs)
const getProxyUrl = (url) => {
  if (!url) return '';
  const backendUrl = getApiBaseUrl();
  
  // Check if it's an S3 URL
  const s3Match = url.match(/https:\/\/([^.]+)\.s3[^\/]*\.amazonaws\.com\/(.+)/);
  if (s3Match) {
    const key = s3Match[2];
    return `${backendUrl}/api/s3-proxy/${key}`;
  }
  
  // Already a proxy URL or relative URL
  if (url.startsWith('/api/') || url.startsWith(backendUrl)) {
    return url.startsWith('/') ? `${backendUrl}${url}` : url;
  }
  
  return url;
};

/**
 * Calculate the rotation delta between current quaternion and rest pose quaternion.
 * Returns the 2D rotation that should be applied to the sprite.
 * 
 * @param {Object} currentQ - Current quaternion {x, y, z, w}
 * @param {Object} restQ - Rest pose quaternion {x, y, z, w}
 * @param {string} partName - Body part name to determine rotation sensitivity
 * @returns {number} Rotation in radians to apply to the sprite
 */
const calculateRotationDelta = (currentQ, restQ, partName) => {
  if (!currentQ || !restQ) return 0;
  
  // Extract Y rotation (swing) from both poses
  const currentYRot = Math.atan2(
    2 * (currentQ.w * currentQ.y + currentQ.z * currentQ.x),
    1 - 2 * (currentQ.x * currentQ.x + currentQ.y * currentQ.y)
  );
  const restYRot = Math.atan2(
    2 * (restQ.w * restQ.y + restQ.z * restQ.x),
    1 - 2 * (restQ.x * restQ.x + restQ.y * restQ.y)
  );
  
  // Extract X rotation (pitch) from both poses
  const currentXRot = Math.asin(Math.max(-1, Math.min(1, 2 * (currentQ.w * currentQ.x - currentQ.z * currentQ.y))));
  const restXRot = Math.asin(Math.max(-1, Math.min(1, 2 * (restQ.w * restQ.x - restQ.z * restQ.y))));
  
  // Extract Z rotation from both poses
  const currentZRot = Math.atan2(
    2 * (currentQ.w * currentQ.z + currentQ.x * currentQ.y),
    1 - 2 * (currentQ.y * currentQ.y + currentQ.z * currentQ.z)
  );
  const restZRot = Math.atan2(
    2 * (restQ.w * restQ.z + restQ.x * restQ.y),
    1 - 2 * (restQ.y * restQ.y + restQ.z * restQ.z)
  );
  
  // Calculate deltas
  const deltaY = currentYRot - restYRot;
  const deltaX = currentXRot - restXRot;
  const deltaZ = currentZRot - restZRot;
  
  // Apply different weights based on body part
  // Higher values = more rotation sensitivity
  let rotation = 0;
  
  if (partName === 'torso') {
    // Torso: significant rotation from Y (twist) and X (lean forward/back)
    // Increased weights for more visible movement
    rotation = deltaY * 0.5 + deltaX * 0.6 + deltaZ * 0.3;
  } else if (partName === 'head') {
    // Head follows torso with additional movement
    rotation = deltaY * 0.4 + deltaX * 0.5 + deltaZ * 0.25;
  } else if (partName.includes('arm_upper')) {
    rotation = deltaY * 0.5 + deltaX * 0.3;
  } else if (partName.includes('arm_lower')) {
    rotation = deltaY * 0.4 + deltaX * 0.3;
  } else if (partName.includes('hand')) {
    rotation = deltaY * 0.35 + deltaX * 0.25;
  } else if (partName.includes('leg_upper')) {
    rotation = deltaY * 0.4 + deltaX * 0.5;
  } else if (partName.includes('leg_lower')) {
    rotation = deltaY * 0.35 + deltaX * 0.4;
  } else if (partName.includes('foot')) {
    rotation = deltaY * 0.3 + deltaX * 0.35;
  }
  
  return rotation;
};

/**
 * Normalize angle to [-180, 180] degrees
 */
const normalizeAngle = (angle) => {
  while (angle > 180) angle -= 360;
  while (angle < -180) angle += 360;
  return angle;
};

/**
 * Select the appropriate sprite angle bucket for a given angle
 */
const selectAngleBucket = (angle, buckets, partName = '') => {
  if (!buckets || buckets.length === 0) return null;
  
  // Only debug log for head part to avoid spam
  const shouldLog = partName === 'head';
  
  for (const bucket of buckets) {
    // Handle wrap-around case (back view)
    if (bucket.range_min > bucket.range_max) {
      if (angle >= bucket.range_min || angle <= bucket.range_max) {
        if (shouldLog) console.log(`[selectAngleBucket] ${partName}: angle=${angle.toFixed(1)}° matched ${bucket.name} (wrap-around)`);
        return bucket;
      }
    } else {
      if (angle >= bucket.range_min && angle <= bucket.range_max) {
        if (shouldLog) console.log(`[selectAngleBucket] ${partName}: angle=${angle.toFixed(1)}° matched ${bucket.name} [${bucket.range_min}, ${bucket.range_max}]`);
        return bucket;
      }
    }
  }
  
  if (shouldLog) console.log(`[selectAngleBucket] ${partName}: angle=${angle.toFixed(1)}° no direct match, finding nearest...`);
  
  // Fallback: find nearest bucket
  let minDistance = Infinity;
  let nearestBucket = buckets[0];
  
  for (const bucket of buckets) {
    let midAngle = (bucket.range_min + bucket.range_max) / 2;
    if (bucket.range_min > bucket.range_max) {
      midAngle = (bucket.range_min + bucket.range_max + 360) / 2;
      if (midAngle > 180) midAngle -= 360;
    }
    
    const distance = Math.abs(normalizeAngle(angle - midAngle));
    if (distance < minDistance) {
      minDistance = distance;
      nearestBucket = bucket;
    }
  }
  
  if (shouldLog) console.log(`[selectAngleBucket] ${partName}: angle=${angle.toFixed(1)}° fallback to nearest: ${nearestBucket.name}`);
  
  return nearestBucket;
};

/**
 * Calculate the camera viewing angle (which direction we're looking at the character from)
 * Returns angle in degrees: 0 = front, 90 = right side, 180/-180 = back, -90 = left side
 * This matches the angle bucket ranges in the sprite configuration.
 */
const calculateCameraViewAngle = (cameraPosition, cameraTarget) => {
  // Vector from target to camera
  const dx = cameraPosition.x - cameraTarget.x;
  const dz = cameraPosition.z - cameraTarget.z;
  
  // Angle in XZ plane (yaw) - where camera is positioned around the character
  const angleRad = Math.atan2(dx, dz);
  const angleDeg = angleRad * (180 / Math.PI);
  
  return normalizeAngle(angleDeg);
};

/**
 * Get position of a bone, finding the best available match
 * Bones may have different naming conventions in different models
 */
const getBonePosition = (boneData, boneName) => {
  // Try exact match first
  let bone = boneData[boneName];
  if (bone?.position) return bone.position;
  
  // Try common variations
  const variations = [
    boneName,
    `mixamorig${boneName}`,
    `mixamorig:${boneName}`,
    boneName.replace('Left', 'L_').replace('Right', 'R_'),
    boneName.toLowerCase()
  ];
  
  for (const name of variations) {
    if (boneData[name]?.position) {
      return boneData[name].position;
    }
  }
  
  return null;
};

/**
 * Get the reference point for positioning (hip/pelvis center)
 */
const getReferencePoint = (boneData) => {
  // Try common hip/pelvis bone names
  const hipBones = ['Hips', 'Pelvis', 'mixamorigHips', 'Root'];
  
  for (const name of hipBones) {
    const pos = getBonePosition(boneData, name);
    if (pos) return pos;
  }
  
  // Fallback: find any bone with 'hip' in name
  for (const [name, transform] of Object.entries(boneData)) {
    if (name.toLowerCase().includes('hip') && transform.position) {
      return transform.position;
    }
  }
  
  return { x: 0, y: 1, z: 0 }; // Default center position
};

/**
 * Generate render instructions from bone data and sprite config
 * 
 * Uses bone positions RELATIVE to a central reference point (hips) 
 * to position sprites correctly on the 2D canvas.
 */
const generateRenderInstructions = (boneData, spriteConfig, cameraPosition, cameraTarget) => {
  const sprites = [];
  
  if (!spriteConfig || !boneData || Object.keys(boneData).length === 0) {
    return sprites;
  }
  
  const { parts, bone_to_part_mapping } = spriteConfig;
  
  // Calculate overall camera view angle (same for all parts)
  const cameraViewAngle = calculateCameraViewAngle(cameraPosition, cameraTarget);
  
  // Get reference point (hip/pelvis) for relative positioning
  const refPoint = getReferencePoint(boneData);
  
  // Calculate model bounds for normalization
  let minY = Infinity, maxY = -Infinity;
  let minX = Infinity, maxX = -Infinity;
  for (const transform of Object.values(boneData)) {
    const y = transform.position?.y || 0;
    const x = transform.position?.x || 0;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
  }
  const modelHeight = Math.max(0.5, maxY - minY);
  const modelWidth = Math.max(0.5, maxX - minX);
  
  // Track which body parts we've already added (to prevent duplicates)
  const processedParts = new Set();
  
  // Iterate through all parts defined in the sprite config
  for (const [partName, partConfig] of Object.entries(parts || {})) {
    // Skip if we've already processed this body part
    if (processedParts.has(partName)) continue;
    
    // Skip if no angle buckets
    if (!partConfig.angle_buckets || partConfig.angle_buckets.length === 0) continue;
    
    // Select sprite based on camera view angle
    const bucket = selectAngleBucket(cameraViewAngle, partConfig.angle_buckets, partName);
    if (!bucket || !bucket.sprite_url) continue;
    
    // Find the bone position for this part
    const primaryBoneName = PRIMARY_BONES[partName];
    const bonePos = primaryBoneName ? getBonePosition(boneData, primaryBoneName) : null;
    
    // Calculate position relative to reference point
    let relativePos = { x: 0, y: 0 };
    if (bonePos) {
      // Position relative to hip center, normalized by model dimensions
      relativePos = {
        x: (bonePos.x - refPoint.x) / (modelWidth || 1),
        y: (bonePos.y - refPoint.y) / (modelHeight || 1)
      };
    }
    
    // Mark this part as processed
    processedParts.add(partName);
    
    // Use LEARNED pivots as primary, fall back to config, then defaults
    const learnedPivot = DEFAULT_PIVOTS[partName];
    const configPivot = partConfig.pivot_point;
    const pivot = learnedPivot || configPivot || [0.5, 0.5];
    
    // Get bone rotation for this part
    const hierarchy = BONE_HIERARCHY[partName];
    const boneName = hierarchy?.bone || primaryBoneName;
    const boneTransform = boneName ? getBoneTransform(boneData, boneName) : null;
    
    // Calculate bone rotation in 2D
    // For IK, we need to extract the rotation that affects the 2D projection
    // When viewing from front (camera at +Z), the bone's local Y rotation affects arm swing,
    // and Z rotation affects side-to-side tilt
    // 
    // NOTE: Bone rotation is now calculated in the render effect where we have access
    // to the rest pose ref for delta calculation. This just returns 0 as placeholder.
    let boneRotation = 0;
    
    // DYNAMIC Z-ORDERING: Calculate z-index based on bone's depth from camera
    // This ensures parts closer to camera appear in front
    const boneWorldPos = boneTransform?.position || bonePos;
    const dynamicZOrder = calculateDynamicZOrder(boneWorldPos, cameraPosition, cameraTarget, partName);
    
    sprites.push({
      partName,
      spriteUrl: bucket.sprite_url,
      relativePosition: relativePos,  // Normalized position relative to hip
      boneWorldPosition: boneWorldPos, // Store world position for IK chain
      refPointY: refPoint.y,          // Reference point Y for ground calculation
      modelHeight: modelHeight,       // Model height for scaling
      pivot: pivot,
      zOrder: dynamicZOrder,          // DYNAMIC z-order based on depth
      rotation: boneRotation,         // Bone rotation for IK (placeholder, calculated in render)
      boneTransform: boneTransform ? { ...boneTransform, boneName } : null,   // Full bone transform with name
      boneName: boneName,             // Store bone name for rest pose lookup
      parentPart: hierarchy?.parent,  // Parent in hierarchy for IK chain
      angleBucket: bucket.name,
      cameraAngle: cameraViewAngle
    });
  }
  
  // Sort by z-order for proper layering
  sprites.sort((a, b) => a.zOrder - b.zOrder);
  
  return sprites;
};

/**
 * Get full bone transform (position and rotation) with fallback variations
 */
const getBoneTransform = (boneData, boneName) => {
  // Try exact match first
  let bone = boneData[boneName];
  if (bone) return bone;
  
  // Try common variations
  const variations = [
    boneName,
    `mixamorig${boneName}`,
    `mixamorig:${boneName}`,
    boneName.replace('Left', 'L_').replace('Right', 'R_'),
    boneName.toLowerCase()
  ];
  
  for (const name of variations) {
    if (boneData[name]) {
      return boneData[name];
    }
  }
  
  return null;
};

/**
 * SpriteRenderer Component
 * 
 * Props:
 * - spriteConfig: Character sprite configuration (parts, bone mappings, etc.)
 * - boneData: Current frame's bone transform data
 * - cameraPosition: { x, y, z }
 * - cameraTarget: { x, y, z }
 * - width: Canvas width
 * - height: Canvas height
 * - backgroundColor: Background color (optional)
 * - scale: Global scale factor (optional)
 * - debug: Show debug info (optional)
 * - editable: Enable drag to reposition sprites (optional)
 * - onOffsetsChange: Callback when user adjusts positions (optional)
 * - onPivotsChange: Callback when user adjusts pivots (optional)
 */
const SpriteRenderer = ({
  spriteConfig,
  boneData,
  cameraPosition = { x: 0, y: 1.5, z: 5 },
  cameraTarget = { x: 0, y: 1, z: 0 },
  width = 800,
  height = 600,
  backgroundColor = 'transparent',
  scale = 1,
  debug = false,
  editable = false,
  onOffsetsChange,
  onPivotsChange,
  className = ''
}) => {
  const canvasRef = useRef(null);
  const [spriteCache, setSpriteCache] = useState(new Map());
  const spriteCacheRef = useRef(new Map()); // Ref for stable access
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState({ loaded: 0, total: 0 });
  const preloadAttemptedRef = useRef(new Set()); // Track which configs we've tried to load
  
  // REST POSE: Store the initial bone rotations to calculate deltas during animation
  const restPoseRef = useRef(null);
  const restPoseCapturedRef = useRef(false);
  
  // User-defined adjustments for each body part
  const [userOffsets, setUserOffsets] = useState({});      // Position offsets in pixels
  const [userPivots, setUserPivots] = useState({});        // Pivot point position (0-1 within sprite)
  const [userScales, setUserScales] = useState({});        // Scale multiplier per part
  const [userRotations, setUserRotations] = useState({});  // Rotation offset in radians
  
  // Drag state: mode can be 'position' | 'pivot' | 'resize' | 'rotate'
  const [dragState, setDragState] = useState(null);
  const [hoveredPart, setHoveredPart] = useState(null);
  const [showExport, setShowExport] = useState(false);
  
  // Store sprite bounds for hit testing
  const spriteBoundsRef = useRef({});
  
  // Keep ref in sync
  useEffect(() => {
    spriteCacheRef.current = spriteCache;
  }, [spriteCache]);
  
  // Capture rest pose when bone data first becomes available
  useEffect(() => {
    if (boneData && Object.keys(boneData).length > 0 && !restPoseCapturedRef.current) {
      // Store the initial bone quaternions as rest pose
      const restPose = {};
      for (const [boneName, transform] of Object.entries(boneData)) {
        if (transform?.quaternion) {
          restPose[boneName] = { ...transform.quaternion };
        }
      }
      restPoseRef.current = restPose;
      restPoseCapturedRef.current = true;
      console.log('[SpriteRenderer] Captured rest pose for', Object.keys(restPose).length, 'bones');
    }
  }, [boneData]);
  
  // Generate render instructions
  const renderInstructions = useMemo(() => {
    const instructions = generateRenderInstructions(boneData, spriteConfig, cameraPosition, cameraTarget);
    
    // Debug logging for camera angle
    if (debug && instructions.length > 0) {
      const cameraAngle = calculateCameraViewAngle(cameraPosition, cameraTarget);
      console.log(`[SpriteRenderer] Camera angle: ${cameraAngle.toFixed(1)}°, Camera pos: (${cameraPosition.x?.toFixed(2)}, ${cameraPosition.y?.toFixed(2)}, ${cameraPosition.z?.toFixed(2)})`);
      console.log(`[SpriteRenderer] Rendered ${instructions.length} sprites:`, instructions.map(i => `${i.partName}:${i.angleBucket}`).join(', '));
    }
    
    return instructions;
  }, [boneData, spriteConfig, cameraPosition, cameraTarget, debug]);
  
  // Preload sprites - stable callback that doesn't depend on spriteCache
  const preloadSprites = useCallback(async (config) => {
    if (!config || !config.parts) return;
    
    // Create a stable key for this config
    const configKey = config.id || JSON.stringify(Object.keys(config.parts));
    if (preloadAttemptedRef.current.has(configKey)) return;
    preloadAttemptedRef.current.add(configKey);
    
    const urls = new Set();
    
    // Collect all sprite URLs
    for (const partConfig of Object.values(config.parts)) {
      if (partConfig.angle_buckets) {
        for (const bucket of partConfig.angle_buckets) {
          if (bucket.sprite_url) {
            urls.add(bucket.sprite_url);
          }
        }
      }
    }
    
    if (urls.size === 0) return;
    
    setIsLoading(true);
    setLoadingProgress({ loaded: 0, total: urls.size });
    
    const currentCache = spriteCacheRef.current;
    const newCache = new Map(currentCache);
    let loaded = 0;
    
    const loadPromises = Array.from(urls).map((url) => {
      return new Promise((resolve) => {
        // Convert to proxy URL to avoid CORS issues
        const proxyUrl = getProxyUrl(url);
        
        // Skip if already cached (check both original and proxy URL)
        if (newCache.has(url) || newCache.has(proxyUrl)) {
          loaded++;
          setLoadingProgress({ loaded, total: urls.size });
          resolve();
          return;
        }
        
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        img.onload = () => {
          // Cache with original URL as key
          newCache.set(url, img);
          loaded++;
          setLoadingProgress({ loaded, total: urls.size });
          console.log(`Loaded sprite: ${url.slice(-40)}`);
          resolve();
        };
        
        img.onerror = (e) => {
          console.warn(`Failed to load sprite: ${proxyUrl}`, e);
          loaded++;
          setLoadingProgress({ loaded, total: urls.size });
          resolve();
        };
        
        // Use proxy URL for loading
        img.src = proxyUrl;
      });
    });
    
    await Promise.all(loadPromises);
    setSpriteCache(newCache);
    setIsLoading(false);
  }, []); // No dependencies - uses refs instead
  
  // Load sprites when config changes
  useEffect(() => {
    if (spriteConfig) {
      preloadSprites(spriteConfig);
    }
  }, [spriteConfig?.id, preloadSprites]);
  
  // Check if point is near a resize handle (corners of sprite)
  const getResizeHandle = useCallback((canvasX, canvasY, bounds) => {
    const handleSize = 12;
    const corners = [
      { name: 'se', x: bounds.right, y: bounds.bottom },  // Bottom-right (main resize)
      { name: 'sw', x: bounds.left, y: bounds.bottom },
      { name: 'ne', x: bounds.right, y: bounds.top },
      { name: 'nw', x: bounds.left, y: bounds.top },
    ];
    
    for (const corner of corners) {
      if (Math.abs(canvasX - corner.x) < handleSize && Math.abs(canvasY - corner.y) < handleSize) {
        return corner.name;
      }
    }
    return null;
  }, []);
  
  // Find which sprite is at a given canvas position
  const findSpriteAtPosition = useCallback((canvasX, canvasY) => {
    const bounds = spriteBoundsRef.current;
    // Check in reverse z-order (top sprites first)
    const sortedParts = Object.entries(bounds).sort((a, b) => (b[1].zOrder || 0) - (a[1].zOrder || 0));
    
    for (const [partName, rect] of sortedParts) {
      // Check resize handles first (slightly outside bounds)
      const handle = getResizeHandle(canvasX, canvasY, rect);
      if (handle) {
        return { partName, handle };
      }
      
      if (canvasX >= rect.left && canvasX <= rect.right &&
          canvasY >= rect.top && canvasY <= rect.bottom) {
        return { partName, handle: null };
      }
    }
    return null;
  }, [getResizeHandle]);
  
  // Mouse event handlers for dragging
  const handleMouseDown = useCallback((e) => {
    if (!editable) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const canvasX = e.clientX - rect.left;
    const canvasY = e.clientY - rect.top;
    
    const hit = findSpriteAtPosition(canvasX, canvasY);
    if (hit) {
      const { partName, handle } = hit;
      const bounds = spriteBoundsRef.current[partName];
      
      let mode = 'position';
      if (handle) {
        mode = 'resize';
      } else if (e.shiftKey) {
        mode = 'pivot';
      } else if (e.altKey) {
        mode = 'rotate';
      }
      
      setDragState({
        partName,
        startX: canvasX,
        startY: canvasY,
        mode,
        handle,
        initialOffset: userOffsets[partName] || { x: 0, y: 0 },
        initialPivot: userPivots[partName] || { x: 0.5, y: 0.5 },
        initialScale: userScales[partName] || 1,
        initialRotation: userRotations[partName] || 0,
        bounds: bounds ? { ...bounds } : null,
        // For pivot mode, store where the click was relative to sprite center
        clickRelativeToCenter: bounds ? {
          x: canvasX - bounds.screenX,
          y: canvasY - bounds.screenY
        } : { x: 0, y: 0 }
      });
      e.preventDefault();
    }
  }, [editable, findSpriteAtPosition, userOffsets, userPivots, userScales, userRotations]);
  
  const handleMouseMove = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const canvasX = e.clientX - rect.left;
    const canvasY = e.clientY - rect.top;
    
    if (dragState) {
      const dx = canvasX - dragState.startX;
      const dy = canvasY - dragState.startY;
      
      if (dragState.mode === 'position') {
        const newOffset = {
          x: dragState.initialOffset.x + dx,
          y: dragState.initialOffset.y + dy
        };
        setUserOffsets(prev => ({
          ...prev,
          [dragState.partName]: newOffset
        }));
      } else if (dragState.mode === 'pivot') {
        // Move anchor point within the sprite (sprite stays in place)
        // Calculate new pivot based on where the mouse is relative to sprite bounds
        const bounds = spriteBoundsRef.current[dragState.partName];
        if (bounds) {
          const spriteWidth = bounds.right - bounds.left;
          const spriteHeight = bounds.bottom - bounds.top;
          
          // Convert mouse position to normalized pivot (0-1)
          const newPivotX = (canvasX - bounds.left) / spriteWidth;
          const newPivotY = (canvasY - bounds.top) / spriteHeight;
          
          // Clamp to reasonable range
          setUserPivots(prev => ({
            ...prev,
            [dragState.partName]: {
              x: Math.max(-0.5, Math.min(1.5, newPivotX)),
              y: Math.max(-0.5, Math.min(1.5, newPivotY))
            }
          }));
          
          // Adjust offset to keep sprite visually in place while pivot moves
          const pivotDeltaX = (newPivotX - (dragState.initialPivot?.x || 0.5)) * spriteWidth;
          const pivotDeltaY = (newPivotY - (dragState.initialPivot?.y || 0.5)) * spriteHeight;
          
          setUserOffsets(prev => ({
            ...prev,
            [dragState.partName]: {
              x: dragState.initialOffset.x + pivotDeltaX,
              y: dragState.initialOffset.y + pivotDeltaY
            }
          }));
        }
      } else if (dragState.mode === 'resize') {
        // Calculate scale based on drag distance
        const bounds = dragState.bounds;
        if (bounds) {
          const originalWidth = bounds.right - bounds.left;
          const originalHeight = bounds.bottom - bounds.top;
          const originalDiag = Math.sqrt(originalWidth * originalWidth + originalHeight * originalHeight);
          
          // Calculate new diagonal based on mouse position
          const newWidth = Math.abs(canvasX - bounds.left);
          const newHeight = Math.abs(canvasY - bounds.top);
          const newDiag = Math.sqrt(newWidth * newWidth + newHeight * newHeight);
          
          const scaleChange = newDiag / originalDiag;
          const newScale = Math.max(0.2, Math.min(3, dragState.initialScale * scaleChange));
          
          setUserScales(prev => ({
            ...prev,
            [dragState.partName]: newScale
          }));
        }
      } else if (dragState.mode === 'rotate') {
        // Calculate rotation based on mouse angle relative to sprite center
        const bounds = spriteBoundsRef.current[dragState.partName];
        if (bounds) {
          // Calculate angle from sprite center to current mouse position
          const centerX = bounds.screenX;
          const centerY = bounds.screenY;
          
          const currentAngle = Math.atan2(canvasY - centerY, canvasX - centerX);
          const startAngle = Math.atan2(dragState.startY - centerY, dragState.startX - centerX);
          
          // Rotation delta
          let rotationDelta = currentAngle - startAngle;
          
          // Calculate new rotation
          const newRotation = dragState.initialRotation + rotationDelta;
          
          setUserRotations(prev => ({
            ...prev,
            [dragState.partName]: newRotation
          }));
        }
      }
    } else if (editable) {
      // Update hover state
      const hit = findSpriteAtPosition(canvasX, canvasY);
      setHoveredPart(hit?.partName || null);
    }
  }, [dragState, editable, findSpriteAtPosition]);
  
  const handleMouseUp = useCallback(() => {
    if (dragState) {
      // Report final values
      if (onOffsetsChange) {
        onOffsetsChange(userOffsets);
      }
      if (onPivotsChange) {
        onPivotsChange(userPivots);
      }
      setDragState(null);
    }
  }, [dragState, userOffsets, userPivots, onOffsetsChange, onPivotsChange]);
  
  const handleMouseLeave = useCallback(() => {
    setHoveredPart(null);
    if (dragState) {
      handleMouseUp();
    }
  }, [dragState, handleMouseUp]);
  
  // Export current settings as JSON
  const exportSettings = useCallback(() => {
    const settings = {
      offsets: userOffsets,
      pivots: userPivots,
      scales: userScales,
      rotations: userRotations,
      timestamp: new Date().toISOString()
    };
    return settings;
  }, [userOffsets, userPivots, userScales, userRotations]);

  // Render sprites to canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Clear canvas
    ctx.clearRect(0, 0, width, height);
    
    // Draw background
    if (backgroundColor && backgroundColor !== 'transparent') {
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, width, height);
    }
    
    if (renderInstructions.length === 0) return;
    
    // Calculate viewport transform
    // Character is centered horizontally, standing on the ground
    const centerX = width / 2;
    const groundY = height * 0.85; // Ground level at 85% down
    const characterHeight = height * 0.70; // Character takes up 70% of canvas height
    
    // Reference point: The hip is typically at ~40% of character height from ground
    // This positions the hip in the middle-ish of the character
    const hipY = groundY - characterHeight * 0.45;
    
    // Clear bounds for hit testing
    const newBounds = {};
    
    // Build a map of part transforms for IK chain calculation
    const partTransforms = {};
    
    // First pass: Calculate BASE transforms for all parts (before IK adjustments)
    for (const instruction of renderInstructions) {
      const sprite = spriteCache.get(instruction.spriteUrl);
      if (!sprite) continue;
      
      // Calculate scale based on this body part's bone distance
      const bodyPartScale = getBodyPartScale(instruction.partName, boneData, sprite, characterHeight);
      const userScale = userScales[instruction.partName] || 1;
      const finalScale = scale * bodyPartScale * userScale;
      
      const spriteWidth = sprite.width * finalScale;
      const spriteHeight = sprite.height * finalScale;
      
      // Calculate screen position using relative bone position
      const relPos = instruction.relativePosition || { x: 0, y: 0 };
      
      let screenX = centerX + relPos.x * characterHeight * 0.5;
      let screenY = hipY - relPos.y * characterHeight;
      
      // Apply learned offsets (scaled proportionally to character height)
      const learnedOffset = LEARNED_OFFSETS[instruction.partName] || { x: 0, y: 0 };
      const offsetScale = characterHeight / REFERENCE_CHARACTER_HEIGHT;
      screenX += learnedOffset.x * offsetScale;
      screenY += learnedOffset.y * offsetScale;
      
      // Apply user manual offset
      const userOffset = userOffsets[instruction.partName] || { x: 0, y: 0 };
      screenX += userOffset.x;
      screenY += userOffset.y;
      
      // Calculate pivot
      const userPivot = userPivots[instruction.partName];
      const basePivotX = instruction.pivot[0] || 0.5;
      const basePivotY = instruction.pivot[1] || 0.5;
      const finalPivotX = userPivot ? userPivot.x : basePivotX;
      const finalPivotY = userPivot ? userPivot.y : basePivotY;
      const pivotX = finalPivotX * spriteWidth;
      const pivotY = finalPivotY * spriteHeight;
      
      // Calculate bone rotation DELTA from rest pose
      // In EDIT mode, disable automatic bone rotation so user can set base positions
      let boneRotation = 0;
      if (!editable && instruction.boneTransform?.quaternion && restPoseRef.current) {
        // Use the bone name from the instruction
        const boneName = instruction.boneName;
        const restQ = restPoseRef.current[boneName];
        const currentQ = instruction.boneTransform.quaternion;
        
        if (restQ && currentQ) {
          boneRotation = calculateRotationDelta(currentQ, restQ, instruction.partName);
        } else if (instruction.partName === 'torso') {
          // Debug: log if torso bone is not found
          console.log('[SpriteRenderer] Torso bone lookup:', {
            boneName,
            hasRestQ: !!restQ,
            hasCurrentQ: !!currentQ,
            availableBones: Object.keys(restPoseRef.current || {}).slice(0, 10)
          });
        }
      }
      
      // Add user rotation offset for manual adjustment
      const userRotation = userRotations[instruction.partName] || 0;
      const totalRotation = boneRotation + userRotation;
      
      // Store transform data for IK calculations
      partTransforms[instruction.partName] = {
        instruction,
        sprite,
        screenX,
        screenY,
        spriteWidth,
        spriteHeight,
        pivotX,
        pivotY,
        finalPivotX,
        finalPivotY,
        rotation: totalRotation,
        finalScale,
        // For IK chain: the "joint end" position (bottom of sprite in local coords)
        // This is where the child part would attach
        jointEndLocalX: (1 - finalPivotX) * spriteWidth * 0.5, // Offset from pivot to center-bottom
        jointEndLocalY: (1 - finalPivotY) * spriteHeight       // Bottom of sprite
      };
    }
    
    // IK Pass: Adjust child positions based on parent's transformed position
    // Process parts in hierarchy order (parents first)
    const hierarchyOrder = [
      'torso',
      'head',
      'arm_upper_left', 'arm_upper_right',
      'arm_lower_left', 'arm_lower_right', 
      'hand_left', 'hand_right',
      'leg_upper_left', 'leg_upper_right',
      'leg_lower_left', 'leg_lower_right',
      'foot_left', 'foot_right'
    ];
    
    // Calculate accumulated transforms for each part
    const accumulatedTransforms = {};
    
    for (const partName of hierarchyOrder) {
      const transform = partTransforms[partName];
      if (!transform) continue;
      
      const parentPartName = transform.instruction.parentPart;
      const parentTransform = parentPartName ? accumulatedTransforms[parentPartName] : null;
      
      // Start with the calculated screen position
      let finalScreenX = transform.screenX;
      let finalScreenY = transform.screenY;
      let finalRotation = transform.rotation;
      
      // If this part has a parent with rotation, we need to adjust the position
      // to maintain the joint connection
      // SKIP IK adjustments in edit mode - show static positions for manual adjustment
      if (!editable && parentTransform && parentTransform.rotation !== 0) {
        // The parent has rotated, so this child's attachment point has moved
        // We need to calculate where the parent's "joint end" is now
        
        const parentRotation = parentTransform.rotation;
        
        // Calculate offset from parent's pivot to the joint connection point
        // (roughly the bottom-center of the parent sprite for limbs)
        const jointOffsetX = 0;
        const jointOffsetY = parentTransform.spriteHeight * (1 - parentTransform.finalPivotY) * 0.8;
        
        // Rotate this offset by the parent's rotation
        const cos = Math.cos(parentRotation);
        const sin = Math.sin(parentRotation);
        const rotatedJointX = jointOffsetX * cos - jointOffsetY * sin;
        const rotatedJointY = jointOffsetX * sin + jointOffsetY * cos;
        
        // The original joint position (without rotation)
        const originalJointX = jointOffsetX;
        const originalJointY = jointOffsetY;
        
        // Adjustment needed due to parent's rotation
        const adjustX = rotatedJointX - originalJointX;
        const adjustY = rotatedJointY - originalJointY;
        
        // Apply adjustment scaled by parent's contribution
        // Use a blend factor to smooth the effect
        const blendFactor = 0.6;
        finalScreenX += adjustX * blendFactor;
        finalScreenY += adjustY * blendFactor;
        
        // Child parts inherit some rotation from their parent chain
        // This helps limbs move as connected units
        const inheritedRotation = parentTransform.rotation * 0.3;
        finalRotation += inheritedRotation;
      }
      
      // Store the final transform for this part (and for its children to use)
      accumulatedTransforms[partName] = {
        ...transform,
        screenX: finalScreenX,
        screenY: finalScreenY,
        rotation: finalRotation
      };
    }
    
    // Second pass: Draw sprites in z-order with IK-adjusted transforms
    for (const instruction of renderInstructions) {
      const baseTransform = partTransforms[instruction.partName];
      if (!baseTransform) continue;
      
      // Use the IK-adjusted transform if available
      const transform = accumulatedTransforms[instruction.partName] || baseTransform;
      
      const { sprite, spriteWidth, spriteHeight, rotation } = transform;
      let { screenX, screenY, pivotX, pivotY } = transform;
      
      // Store bounds for hit testing
      newBounds[instruction.partName] = {
        left: screenX - pivotX,
        top: screenY - pivotY,
        right: screenX - pivotX + spriteWidth,
        bottom: screenY - pivotY + spriteHeight,
        zOrder: instruction.zOrder,
        screenX,
        screenY,
        pivotX,
        pivotY,
        spriteWidth,
        spriteHeight
      };
      
      ctx.save();
      
      // Move to screen position (where the pivot/joint is)
      ctx.translate(screenX, screenY);
      
      // Apply bone rotation around the pivot point
      if (rotation) {
        ctx.rotate(rotation);
      }
      
      // Draw sprite centered on pivot
      ctx.drawImage(
        sprite,
        -pivotX,
        -pivotY,
        spriteWidth,
        spriteHeight
      );
      
      // Editable mode overlays
      if (editable) {
        const isHovered = hoveredPart === instruction.partName;
        const isDragging = dragState?.partName === instruction.partName;
        
        if (isHovered || isDragging) {
          // Draw selection border
          ctx.strokeStyle = isDragging ? '#00ff00' : '#ffff00';
          ctx.lineWidth = 2;
          ctx.setLineDash(isDragging ? [] : [5, 3]);
          ctx.strokeRect(-pivotX, -pivotY, spriteWidth, spriteHeight);
          ctx.setLineDash([]);
          
          // Draw resize handles at corners
          const handleSize = 8;
          ctx.fillStyle = dragState?.mode === 'resize' ? '#00ffff' : '#ffffff';
          const corners = [
            { x: -pivotX, y: -pivotY },
            { x: -pivotX + spriteWidth, y: -pivotY },
            { x: -pivotX, y: -pivotY + spriteHeight },
            { x: -pivotX + spriteWidth, y: -pivotY + spriteHeight }
          ];
          for (const corner of corners) {
            ctx.fillRect(corner.x - handleSize/2, corner.y - handleSize/2, handleSize, handleSize);
            ctx.strokeStyle = '#000000';
            ctx.lineWidth = 1;
            ctx.strokeRect(corner.x - handleSize/2, corner.y - handleSize/2, handleSize, handleSize);
          }
          
          // Draw rotation indicator arc (when hovered or rotating)
          const rotRadius = Math.max(spriteWidth, spriteHeight) * 0.4;
          ctx.strokeStyle = dragState?.mode === 'rotate' ? '#00ffff' : 'rgba(255,255,255,0.5)';
          ctx.lineWidth = dragState?.mode === 'rotate' ? 3 : 1;
          ctx.beginPath();
          ctx.arc(0, 0, rotRadius, 0, Math.PI * 2);
          ctx.stroke();
          
          // Draw rotation line indicator
          const currentRotOffset = userRotations[instruction.partName] || 0;
          ctx.strokeStyle = '#00ffff';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(0, 0);
          ctx.lineTo(Math.cos(currentRotOffset - Math.PI/2) * rotRadius, Math.sin(currentRotOffset - Math.PI/2) * rotRadius);
          ctx.stroke();
          
          // Draw anchor point (larger, more visible)
          ctx.fillStyle = dragState?.mode === 'pivot' ? '#ff00ff' : '#ff0000';
          ctx.beginPath();
          ctx.arc(0, 0, 8, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = 'white';
          ctx.lineWidth = 2;
          ctx.stroke();
          
          // Draw crosshair at anchor
          ctx.strokeStyle = 'white';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(-12, 0);
          ctx.lineTo(12, 0);
          ctx.moveTo(0, -12);
          ctx.lineTo(0, 12);
          ctx.stroke();
          
          // Show part name label with rotation info
          const rotDeg = ((userRotations[instruction.partName] || 0) * 180 / Math.PI).toFixed(0);
          const labelText = `${instruction.partName} (${rotDeg}°)`;
          ctx.fillStyle = 'rgba(0,0,0,0.7)';
          ctx.fillRect(-pivotX, -pivotY - 18, spriteWidth, 16);
          ctx.fillStyle = '#ffffff';
          ctx.font = '11px sans-serif';
          ctx.fillText(labelText, -pivotX + 4, -pivotY - 5);
        }
      }
      
      // Debug overlay
      if (debug && !editable) {
        // Draw pivot point
        ctx.fillStyle = 'red';
        ctx.beginPath();
        ctx.arc(0, 0, 4, 0, Math.PI * 2);
        ctx.fill();
        
        // Draw crosshair
        ctx.strokeStyle = 'lime';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(-15, 0);
        ctx.lineTo(15, 0);
        ctx.moveTo(0, -15);
        ctx.lineTo(0, 15);
        ctx.stroke();
        
        // Labels
        ctx.fillStyle = 'white';
        ctx.font = '10px monospace';
        ctx.shadowColor = 'black';
        ctx.shadowBlur = 2;
        ctx.fillText(`${instruction.partName}`, 8, -3);
        ctx.fillText(`${instruction.angleBucket || '?'}`, 8, 9);
      }
      
      ctx.restore();
    }
    
    // Update bounds ref
    spriteBoundsRef.current = newBounds;
    
    // Debug: Show render info
    if (debug) {
      ctx.fillStyle = 'white';
      ctx.font = '12px monospace';
      ctx.shadowColor = 'black';
      ctx.shadowBlur = 3;
      
      // Calculate camera angle for debug display
      const cameraAngle = calculateCameraViewAngle(cameraPosition, cameraTarget);
      const angleBucket = renderInstructions[0]?.angleBucket || 'none';
      
      ctx.fillText(`Sprites: ${renderInstructions.length}`, 10, 20);
      ctx.fillText(`Cached: ${spriteCache.size}`, 10, 35);
      ctx.fillText(`Camera Angle: ${cameraAngle.toFixed(1)}°`, 10, 50);
      ctx.fillText(`Angle Bucket: ${angleBucket}`, 10, 65);
      
      // List body parts being rendered
      const partNames = renderInstructions.map(i => i.partName).join(', ');
      if (partNames.length > 60) {
        ctx.fillText(`Parts: ${partNames.slice(0, 57)}...`, 10, 80);
      } else {
        ctx.fillText(`Parts: ${partNames || 'none'}`, 10, 80);
      }
    }
    
    // Editable mode instructions
    if (editable) {
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.font = '11px sans-serif';
      ctx.shadowColor = 'black';
      ctx.shadowBlur = 3;
      ctx.fillText('Drag: move | Shift+Drag: anchor | Alt+Drag: rotate | Corners: resize', 10, height - 10);
    }
    
  }, [renderInstructions, spriteCache, width, height, backgroundColor, scale, debug, editable, cameraPosition, cameraTarget, boneData, userOffsets, userPivots, userScales, userRotations, hoveredPart, dragState]);
  
  // Determine cursor style based on context
  const getCursor = () => {
    if (!editable) return 'default';
    if (dragState) {
      if (dragState.mode === 'resize') return 'nwse-resize';
      if (dragState.mode === 'rotate') return 'grab';
      if (dragState.mode === 'pivot') return 'crosshair';
      return 'grabbing';
    }
    if (hoveredPart) return 'grab';
    return 'default';
  };
  
  return (
    <div className={`relative ${className}`}>
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        className="block"
        style={{ 
          backgroundColor: backgroundColor === 'transparent' ? 'transparent' : undefined,
          cursor: getCursor()
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      />
      
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <div className="text-center text-white">
            <div className="text-sm mb-2">Loading sprites...</div>
            <div className="w-32 h-2 bg-gray-700 rounded overflow-hidden">
              <div 
                className="h-full bg-purple-500 transition-all"
                style={{ width: `${(loadingProgress.loaded / loadingProgress.total) * 100}%` }}
              />
            </div>
            <div className="text-xs mt-1">
              {loadingProgress.loaded} / {loadingProgress.total}
            </div>
          </div>
        </div>
      )}
      
      {/* Edit mode controls */}
      {editable && (
        <div className="absolute top-2 right-2 flex gap-2">
          <button
            onClick={() => setShowExport(true)}
            className="bg-blue-500 hover:bg-blue-600 text-white text-xs px-3 py-1 rounded font-medium"
          >
            Export Settings
          </button>
          <div className="bg-yellow-500 text-black text-xs px-2 py-1 rounded font-medium">
            EDIT MODE
          </div>
        </div>
      )}
      
      {/* Display current values when editing */}
      {editable && dragState && (
        <div className="absolute top-2 left-2 bg-black/80 text-white text-xs px-2 py-1 rounded font-mono">
          {dragState.partName}: {
            dragState.mode === 'position' 
              ? `offset (${(userOffsets[dragState.partName]?.x || 0).toFixed(0)}, ${(userOffsets[dragState.partName]?.y || 0).toFixed(0)})`
              : dragState.mode === 'pivot'
              ? `pivot (${(userPivots[dragState.partName]?.x || 0.5).toFixed(2)}, ${(userPivots[dragState.partName]?.y || 0.5).toFixed(2)})`
              : dragState.mode === 'rotate'
              ? `rotation ${((userRotations[dragState.partName] || 0) * 180 / Math.PI).toFixed(1)}°`
              : `scale ${(userScales[dragState.partName] || 1).toFixed(2)}x`
          }
        </div>
      )}
      
      {/* Export Settings Modal */}
      {showExport && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setShowExport(false)}>
          <div className="bg-slate-800 rounded-lg p-4 max-w-2xl max-h-[80vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white text-lg font-semibold">Sprite Arrangement Settings</h3>
              <button 
                onClick={() => setShowExport(false)}
                className="text-slate-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            <p className="text-slate-400 text-sm mb-3">
              Copy this JSON and share it with me so I can analyze your adjustments:
            </p>
            <pre className="bg-slate-900 text-green-400 p-3 rounded text-xs overflow-auto max-h-96 select-all">
{JSON.stringify(exportSettings(), null, 2)}
            </pre>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(JSON.stringify(exportSettings(), null, 2));
                }}
                className="bg-blue-500 hover:bg-blue-600 text-white text-sm px-4 py-2 rounded"
              >
                Copy to Clipboard
              </button>
              <button
                onClick={() => {
                  // Reset all adjustments
                  setUserOffsets({});
                  setUserPivots({});
                  setUserScales({});
                  setUserRotations({});
                }}
                className="bg-red-500 hover:bg-red-600 text-white text-sm px-4 py-2 rounded"
              >
                Reset All
              </button>
              <button
                onClick={() => setShowExport(false)}
                className="bg-slate-600 hover:bg-slate-500 text-white text-sm px-4 py-2 rounded"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * SpritePreview - Animated preview component
 * 
 * Plays through animation frames and renders sprites.
 */
export const SpritePreview = ({
  spriteConfig,
  animationFrames, // Array of bone data per frame
  fps = 24,
  loop = true,
  playing = false,
  onFrameChange,
  ...rendererProps
}) => {
  const [currentFrame, setCurrentFrame] = useState(0);
  const animationRef = useRef(null);
  const lastTimeRef = useRef(0);
  
  // Animation loop
  useEffect(() => {
    if (!playing || !animationFrames || animationFrames.length === 0) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      return;
    }
    
    const frameDuration = 1000 / fps;
    
    const animate = (time) => {
      if (time - lastTimeRef.current >= frameDuration) {
        setCurrentFrame((prev) => {
          const next = prev + 1;
          if (next >= animationFrames.length) {
            return loop ? 0 : prev;
          }
          return next;
        });
        lastTimeRef.current = time;
      }
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [playing, animationFrames, fps, loop]);
  
  // Notify parent of frame changes
  useEffect(() => {
    if (onFrameChange) {
      onFrameChange(currentFrame);
    }
  }, [currentFrame, onFrameChange]);
  
  // Get current frame's bone data
  const currentBoneData = animationFrames?.[currentFrame]?.bones || {};
  
  return (
    <SpriteRenderer
      spriteConfig={spriteConfig}
      boneData={currentBoneData}
      {...rendererProps}
    />
  );
};

export default SpriteRenderer;
